import type {
  AddConjugeSeguradoDataRequest,
  AddConjugeSeguradoDataResponse,
  AddSeguradoDataRequest,
  AddSeguradoDataResponse,
  BffSeguradoMessageResponse,
  GetConsultarSeguradosFaturamentoParams,
  GetConsultarSeguradosFaturamentoResponse,
  GetSeguradoDetailParams,
  GetSeguradoDetailResponse,
  GetSeguradoListParams,
  GetSeguradoListResponse,
  RemoveSeguradoDataRequest,
  RemoveSeguradoDataResponse,
  UpdateSeguradoDataRequest,
  UpdateSeguradoDataResponse,
} from '../../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import { VIDA_SEGURADO_MAINTENANCE_ROUTES } from '../requestsService';

const buildQueryString = (queryParams: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const unwrapBffSeguradoMessageResponse = <T>(
  data: BffSeguradoMessageResponse<T> | BffSeguradoMessageResponse<T>[],
): BffSeguradoMessageResponse<T> => {
  if (Array.isArray(data)) {
    return data[0] ?? { mensagem: null, retorno: null as T };
  }
  return data;
};

export class MaintenanceSeguradoService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Includes segurado data for billing.
   * Backend: /v1/bff/vida/segurado/incluir-segurados-faturamento
   */
  async addSeguradoData(
    request: AddSeguradoDataRequest,
  ): Promise<AddSeguradoDataResponse> {
    const response = await this.fetch<AddSeguradoDataResponse | AddSeguradoDataResponse[]>(
      VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_ADD_SEGURADO_MAINTENANCE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return unwrapBffSeguradoMessageResponse(response);
  }

  /**
   * Includes spouse segurado data for billing.
   * Backend: /v1/bff/vida/segurado/incluir-conjugue-segurados-faturamento
   */
  async addConjugeSeguradoData(
    request: AddConjugeSeguradoDataRequest,
  ): Promise<AddConjugeSeguradoDataResponse> {
    const response = await this.fetch<
      AddConjugeSeguradoDataResponse | AddConjugeSeguradoDataResponse[]
    >(
      VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_ADD_CONJUGUE_SEGURADO_MAINTENANCE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return unwrapBffSeguradoMessageResponse(response);
  }

  /**
   * Updates segurado data for billing.
   * Backend: /v1/bff/vida/segurado/alterar-segurados-faturamento
   */
  async updateSeguradoData(
    request: UpdateSeguradoDataRequest,
  ): Promise<UpdateSeguradoDataResponse> {
    const response = await this.fetch<UpdateSeguradoDataResponse | UpdateSeguradoDataResponse[]>(
      VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_ALTER_SEGURADO_MAINTENANCE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return unwrapBffSeguradoMessageResponse(response);
  }

  /**
   * Removes segurado data from billing.
   * Backend: /v1/bff/vida/segurado/excluir-segurados-faturamento
   */
  async removeSeguradoData(
    request: RemoveSeguradoDataRequest,
  ): Promise<RemoveSeguradoDataResponse> {
    const response = await this.fetch<RemoveSeguradoDataResponse | RemoveSeguradoDataResponse[]>(
      VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_REMOVE_SEGURADO_MAINTENANCE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return unwrapBffSeguradoMessageResponse(response);
  }

  /**
   * Fetches paginated segurado list for a subestipulante.
   * Backend: /v1/bff/vida/segurado/obter-lista-segurado
   */
  async getSeguradoList(
    params: GetSeguradoListParams,
  ): Promise<GetSeguradoListResponse> {
    const url =
      VIDA_SEGURADO_MAINTENANCE_ROUTES.GET_LIST_SEGURADO_MAINTENANCE +
      buildQueryString({
        identSubestipulante: params.identSubestipulante,
        identCertificado: params.identCertificado,
        codMatriculaSeg: params.codMatriculaSeg,
        tipoSegurado: params.tipoSegurado,
        identContrato: params.identContrato,
        nomeSegurado: params.nomeSegurado,
        numDocumento: params.numDocumento,
        tipoMovimentoVida: params.tipoMovimentoVida,
        page: params.page,
        size: params.size,
      });

    return this.fetch<GetSeguradoListResponse>(url, { method: 'GET' });
  }

  /**
   * Consults segurados for billing by subestipulante.
   * Backend: /v1/bff/vida/segurado/consultar-segurados-faturamento
   */
  async consultarSeguradosFaturamento(
    params: GetConsultarSeguradosFaturamentoParams,
  ): Promise<GetConsultarSeguradosFaturamentoResponse> {
    const url =
      VIDA_SEGURADO_MAINTENANCE_ROUTES.GET_CONSULTAR_SEGURADOS_FATURAMENTO +
      buildQueryString({
        identSubestipulante: params.identSubestipulante,
        matricula: params.matricula,
        nomeSegurado: params.nomeSegurado,
      });

    const response = await this.fetch<
      GetConsultarSeguradosFaturamentoResponse | GetConsultarSeguradosFaturamentoResponse[]
    >(url, { method: 'GET' });
    return unwrapBffSeguradoMessageResponse(response);
  }

  /**
   * Fetches segurado billing detail.
   * Backend: /v1/bff/vida/segurado/obter-detalhe-segurado-faturamento
   */
  async getSeguradoDetail(
    params: GetSeguradoDetailParams,
  ): Promise<GetSeguradoDetailResponse> {
    const url =
      VIDA_SEGURADO_MAINTENANCE_ROUTES.GET_SEGURADO_MAINTENANCE_DETAIL +
      buildQueryString({
        numVidaPreviaFat: params.numVidaPreviaFat,
      });

    const response = await this.fetch<GetSeguradoDetailResponse | GetSeguradoDetailResponse[]>(
      url,
      { method: 'GET' },
    );
    return unwrapBffSeguradoMessageResponse(response);
  }
}
